// components/members/member/member.component.jsx
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import {
  FaCrown,
  FaUserShield,
  FaUsers,
  FaUserTie,
  FaEdit,
  FaEye,
  FaComments,
  FaChevronDown,
  FaTrashAlt,
} from "react-icons/fa";
import MemberInfoTip from "../MemberInfoTip";
import RetroAvatar from "../../retro/avatar/avatar.component";
import RetroButton from "../../retro/button/retro-button.component";
import SearchPeople from "../../socials/profile/SearchPeople";
import BoxLayer from "../../retro/box-layer/box-layer.component";
import TinyContextMenu from "../../tiny-context-menu/TinyContextMenu";
import permissions from "../../../utils/permissions";
import {
  changeWorkspaceRole,
  transferWorkspaceOwnership,
  removeWorkspaceMember,
  changeProjectRole,
  removeProjectMember,
  createInvite,
} from "../../../firebase/memberFunctions";
import "./member.styles.scss";

const {
  ROLE_LABELS,
  getPermissions,
  getWorkspaceRole,
  getProjectWorkspaceRole,
  getAssignedProjectRoleKey,
  canManageWorkspacePerson,
  canManageProjectPerson,
  getInviteDescription,
} = permissions;
const icons = {
  owner: FaCrown,
  admin: FaUserShield,
  member: FaUsers,
  manager: FaUserTie,
  editor: FaEdit,
  viewer: FaEye,
  viewerandchat: FaComments,
};

// Used for saved roles and the invitation form; keeps their labels/icons identical.
export function RoleControl({
  value,
  choices = [],
  onSelect,
  disabled = false,
}) {
  const Icon = icons[value] || FaUsers;
  const content = (
    <span className="member__role-label">
      <Icon aria-hidden="true" />
      <span>{ROLE_LABELS[value] || "No access"}</span>
      {onSelect && <FaChevronDown size={10} aria-hidden="true" />}
    </span>
  );
  if (!onSelect)
    return (
      <span className={`member__role member__role--${value}`}>{content}</span>
    );
  return (
    <span className="member__role" title="Change role">
      <TinyContextMenu
        customBtn={content}
        minWidth={190}
        items={choices.map((key) => {
          const ItemIcon = icons[key];
          return {
            key,
            label: `${ROLE_LABELS[key]}${key === value ? " ✓" : ""}`,
            icon: <ItemIcon />,
            disabled: disabled || key === value,
            onPress: () => onSelect(key),
          };
        })}
      />
    </span>
  );
}

export function InviteMemberPicker({
  space,
  station = null,
  pending = [],
  onClose,
}) {
  const currentUser = useSelector((state) => state.user.currentUser);
  const access = getPermissions(space, station, currentUser?.uid);
  const [selectedRole, setSelectedRole] = useState(
    station ? "editor" : "member",
  );
  const [selfRole, setSelfRole] = useState("manager");
  const [reminder, setReminder] = useState(false);
  const reminded = useRef(false);
  const [assigned, setAssigned] = useState({});
  const [busyUid, setBusyUid] = useState(null);
  const [sent, setSent] = useState(new Set());
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const busyRef = useRef(false);
  const dialogRef = useRef(null);
  const triggerRef = useRef(document.activeElement);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  // Keep the picker in place, return focus to its trigger, and support Escape/Tab.
  useEffect(() => {
    const previousFocus = triggerRef.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.querySelector("input")?.focus();
    const onKeyDown = (event) => {
      // TinyContextMenu handles its own Escape while its portal is open.
      if (document.querySelector(".tiny-context-menu__menu")) return;
      if (event.key === "Escape") {
        event.preventDefault();
        if (!busyRef.current) closeRef.current();
      }
      if (event.key === "Tab") {
        const controls = [
          ...(dialogRef.current?.querySelectorAll(
            "button:not(:disabled), input:not(:disabled), [tabindex='0']",
          ) || []),
        ];
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);

  const choices = station
    ? ["editor", "manager", "viewer", "viewerandchat"]
    : access.canManageAdmins
      ? ["member", "admin"]
      : ["member"];
  const roleKey = choices.includes(selectedRole) ? selectedRole : choices[0];
  const invitation = {
    targetType: station ? "station" : "space",
    role: roleKey === "viewerandchat" ? "viewer" : roleKey,
    allowChat: roleKey === "viewerandchat",
  };
  const canInvite = station
    ? access.canManageProjectMembers
    : access.canManageWorkspaceMembers;
  const pendingUids = new Set(
    pending
      .filter(
        (item) =>
          item.status === "pending" &&
          item.spaceId === space.spaceId &&
          item.targetType === invitation.targetType &&
          (!station || item.stationId === station.stationId),
      )
      .map((item) => item.inviteeUid),
  );

  const close = () => {
    if (!busyRef.current) onClose();
  };
  async function assignPerson(user, selected = roleKey) {
    if (busyRef.current || !canInvite) return;
    if (
      user.uid === currentUser.uid &&
      selected !== "manager" &&
      !["owner", "admin"].includes(getWorkspaceRole(space, user.uid)) &&
      !window.confirm(
        "You will lose project member-management access. Continue?",
      )
    )
      return;
    busyRef.current = true;
    setBusyUid(user.uid);
    setError("");
    try {
      await changeProjectRole(
        space.spaceId,
        station.stationId,
        user.uid,
        selected,
      );
      setAssigned((old) => ({ ...old, [user.uid]: selected }));
      setMessage(
        `${user.userName || "You"} assigned as ${ROLE_LABELS[selected]}.`,
      );
      if (user.uid === currentUser.uid) setReminder(false);
    } catch (err) {
      setError(err.message || "Could not assign member.");
    } finally {
      busyRef.current = false;
      setBusyUid(null);
    }
  }
  async function invitePerson(user) {
    if (busyRef.current || !canInvite) return;
    busyRef.current = true;
    setBusyUid(user.uid);
    setError("");
    setMessage("");
    try {
      const result = await createInvite({
        ...invitation,
        spaceId: space.spaceId,
        stationId: station?.stationId,
        inviteeUid: user.uid,
      });
      if (
        !result.alreadyPending &&
        station &&
        getWorkspaceRole(space, currentUser.uid) &&
        !(station.members || []).includes(currentUser.uid) &&
        !assigned[currentUser.uid] &&
        !reminded.current
      ) {
        reminded.current = true;
        setReminder(true);
      }
      setSent((previous) => new Set([...previous, user.uid]));
      setMessage(
        result.alreadyPending
          ? `An invitation to ${user.userName || "this person"} is already pending.`
          : `Invitation sent to ${user.userName || "this person"} as ${ROLE_LABELS[roleKey]}.`,
      );
    } catch (err) {
      setError(err.message || "Couldn't send invitation.");
    } finally {
      busyRef.current = false;
      setBusyUid(null);
    }
  }

  return createPortal(
    <div
      className="members-picker-layer"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) close();
      }}
    >
      <div
        ref={dialogRef}
        className="members__invite-picker"
        role="dialog"
        aria-modal="true"
        aria-label={station ? "Invite to project" : "Invite to workspace"}
      >
        <SearchPeople
          embedded
          includeSelf={Boolean(station)}
          disableRowPress
          title={`Add members to ${station?.name || space.name || (station ? "project" : "workspace")}`}
          description="Assign workspace members directly, or invite someone from outside the workspace."
          backButtonAction={close}
          renderPreInformation={() => (
            <>
              {station &&
                !(station.members || []).includes(currentUser.uid) &&
                !assigned[currentUser.uid] && (
                  <div className="members__self-assignment">
                    {reminder && (
                      <div role="status">
                        <p>
                          Invitation sent. Would you like to add yourself to
                          this project too? Workspace assignments appear in
                          Collab Projects when “Show workspace projects” is
                          enabled.
                        </p>
                        <button
                          type="button"
                          onClick={() => setReminder(false)}
                        >
                          Not now
                        </button>
                      </div>
                    )}
                    <div className="members__invite-role">
                      <span>Assign yourself as</span>
                      <RoleControl
                        value={selfRole}
                        choices={[
                          "manager",
                          "editor",
                          "viewer",
                          "viewerandchat",
                        ]}
                        onSelect={setSelfRole}
                        disabled={Boolean(busyUid)}
                      />
                    </div>
                    <button
                      type="button"
                      disabled={Boolean(busyUid) || !canInvite}
                      onClick={() => assignPerson(currentUser, selfRole)}
                    >
                      Assign myself
                    </button>
                  </div>
                )}
              <div className="members__invite-role">
                <span>{station ? "Invite / assign as" : "Invite as"}</span>
                <RoleControl
                  value={roleKey}
                  choices={choices}
                  onSelect={setSelectedRole}
                  disabled={Boolean(busyUid)}
                />
              </div>
              <p className="members__help">
                {getInviteDescription(invitation)}
              </p>
              {!canInvite && (
                <p role="alert">
                  You no longer have permission to invite members here.
                </p>
              )}
              {!!error && (
                <p role="alert" className="members__error">
                  {error}
                </p>
              )}
              {!!message && (
                <p role="status" className="members__notice">
                  {message}
                </p>
              )}
            </>
          )}
          renderRowAction={({ user }) => {
            const workspaceRole = getWorkspaceRole(space, user.uid);
            const direct =
              assigned[user.uid] ||
              ((station?.members || []).includes(user.uid) &&
                getAssignedProjectRoleKey(space, station, user.uid));
            const invited = sent.has(user.uid) || pendingUids.has(user.uid);
            if (station && (workspaceRole || direct)) {
              if (direct === roleKey)
                return (
                  <span className="members__person-status">
                    Assigned · {ROLE_LABELS[direct]}
                  </span>
                );
              return (
                <button
                  type="button"
                  className="members__send-invite"
                  disabled={Boolean(busyUid) || !canInvite}
                  onClick={() => assignPerson(user)}
                >
                  {busyUid === user.uid
                    ? "Saving…"
                    : direct
                      ? "Update role"
                      : "Assign to project"}
                </button>
              );
            }
            if (!station && workspaceRole)
              return (
                <span className="members__person-status">Workspace member</span>
              );
            if (invited)
              return <span className="members__person-status">Invited</span>;
            return (
              <button
                type="button"
                className="members__send-invite"
                disabled={Boolean(busyUid) || !canInvite}
                onClick={() => invitePerson(user)}
              >
                {busyUid === user.uid ? "Sending…" : "Invite"}
              </button>
            );
          }}
        />
      </div>
    </div>,
    document.body,
  );
}

export default function Member({
  member,
  activeSpaceData,
  activeSpaceId,
  station = null,
  compact = false,
}) {
  const currentUser = useSelector((state) => state.user.currentUser);
  const navigate = useNavigate();
  const space = { ...activeSpaceData, spaceId: activeSpaceId };
  const actorUid = currentUser?.uid;
  const access = getPermissions(space, station, actorUid);
  const [confirmation, setConfirmation] = useState(null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [error, setError] = useState("");
  const inherited = station
    ? getProjectWorkspaceRole(space, station, member.uid)
    : null;
  const role = station
    ? getAssignedProjectRoleKey(space, station, member.uid)
    : getWorkspaceRole(space, member.uid);
  const canManage = station
    ? canManageProjectPerson(space, station, actorUid, member.uid)
    : canManageWorkspacePerson(space, actorUid, member.uid);
  const editable = station ? canManage : access.canManageAdmins && canManage;
  const directMember = station && (station.members || []).includes(member.uid);

  async function run(action) {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError("");
    try {
      await action();
      setConfirmation(null);
    } catch (err) {
      setError(err.message || "Couldn't save change.");
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }
  function selectRole(key) {
    if (key === role) return;
    if (
      station &&
      member.uid === actorUid &&
      key !== "manager" &&
      !["owner", "admin"].includes(inherited) &&
      !window.confirm(
        "You will lose project member-management access. A Project Manager or workspace administrator will need to restore it. Continue?",
      )
    )
      return;
    run(() =>
      station
        ? changeProjectRole(activeSpaceId, station.stationId, member.uid, key)
        : changeWorkspaceRole(activeSpaceId, member.uid, key),
    );
  }
  const menu = [];
  const inviter =
    station &&
    (member.sharedWith || []).find(
      (ref) =>
        ref.fromSpaceId === activeSpaceId &&
        ref.stationId === station.stationId,
    )?.invitedBy;
  if (inviter)
    menu.push({
      key: "added-by",
      label:
        inviter === actorUid ? "Added by you" : "View who added this member",
      icon: <FaUserTie />,
      onPress: () => {
        if (!(currentUser?.blockedUsers || []).includes(inviter))
          navigate(`/profile/${inviter}`);
      },
    });
  if (canManage && (!station || directMember))
    menu.push({
      key: "remove",
      label: station ? "Remove from project" : "Remove from workspace",
      icon: <FaTrashAlt />,
      disabled: busy,
      onPress: () => {
        setError("");
        setConfirmation("remove");
      },
    });
  if (!station && access.canTransferOwnership && member.uid !== actorUid)
    menu.push({
      key: "transfer",
      label: "Transfer ownership",
      icon: <FaCrown />,
      disabled: busy,
      onPress: () => {
        setError("");
        setConfirmation("transfer");
      },
    });
  function confirmAction() {
    return run(() =>
      confirmation === "transfer"
        ? transferWorkspaceOwnership(activeSpaceId, member.uid)
        : station
          ? removeProjectMember(activeSpaceId, station.stationId, member.uid)
          : removeWorkspaceMember(activeSpaceId, member.uid),
    );
  }

  return (
    <>
      <div
        className={`${station ? "prmember" : "member"}${compact ? " member--compact" : ""}`}
      >
        <button
          type="button"
          className={station ? "prmember__user" : "member__user"}
          onClick={() => {
            if ((currentUser?.blockedUsers || []).includes(member.uid)) return;
            navigate(`/profile/${member.uid}`);
          }}
        >
          <div className="member__avatar">
            <RetroAvatar
              user={member}
              src={member.imageUrl}
              size={compact ? 32 : 40}
            />
          </div>
          <span className="member__name">{member.userName || member.uid}</span>
        </button>
        <div className="member__controls" aria-busy={busy}>
          {station && ["owner", "admin"].includes(inherited) && (
            <MemberInfoTip message="This is the assigned responsibility. Workspace owners and admins retain administrative powers." />
          )}
          <RoleControl
            value={role}
            disabled={busy}
            choices={
              station
                ? ["manager", "editor", "viewer", "viewerandchat"]
                : ["admin", "member"]
            }
            onSelect={editable ? selectRole : undefined}
          />
          {menu.length > 0 && <TinyContextMenu items={menu} />}
        </div>
      </div>
      {!!error && !confirmation && (
        <p role="alert" className="members__error">
          {error}
        </p>
      )}
      <AnimatePresence>
        {confirmation && (
          <BoxLayer
            setLayer={() => {
              if (!busyRef.current) setConfirmation(null);
            }}
          >
            <div className="members__confirmation">
              <h3>
                {confirmation === "transfer"
                  ? "Transfer workspace ownership?"
                  : `Remove ${member.userName || "this member"}?`}
              </h3>
              <p>
                {confirmation === "transfer"
                  ? `${member.userName || "This member"} will become the owner. You will become an Admin.`
                  : !station
                    ? "Workspace access will be removed. Separate project memberships will remain."
                    : ["owner", "admin"].includes(inherited)
                      ? "Only the explicit assignment is removed. Workspace administrative access remains."
                      : inherited
                        ? "Removing this assignment restores inherited Editor access, including editing, chat and calls—even for a Viewer."
                        : "Project access, including its chat and calls, will be removed."}
              </p>
              {!!error && (
                <p role="alert" className="members__error">
                  {error}
                </p>
              )}
              <div className="member__deleteBtns">
                <RetroButton
                  type="button"
                  mode="gray"
                  disabled={busy}
                  onClick={() => setConfirmation(null)}
                >
                  Cancel
                </RetroButton>
                <RetroButton
                  type="button"
                  disabled={busy}
                  onClick={confirmAction}
                >
                  {busy ? "Saving…" : "Confirm"}
                </RetroButton>
              </div>
            </div>
          </BoxLayer>
        )}
      </AnimatePresence>
    </>
  );
}
